import { useCallback, useEffect, useMemo, useState } from 'react';
import { GagueChartItem, OptionKey, SpendPeriodType } from '@common-fe/common-fe';

import { NodeOption } from '@/common/types';
import theme from '@/styles/theme';

import { useGetSpendLimitPeriod, useGetSpendLimitPeriodRemaining } from './';

interface SpendPeriodTrackerProps {
  accountId?: string;
}

const useSpendPeriodTracker = ({ accountId }: SpendPeriodTrackerProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState<OptionKey>('');
  const [gagueChartValue, setGagueChartValue] = useState<GagueChartItem[]>([
    {
      percents: 0,
      color: theme.colors.cardTypes.TRANSIT,
    },
  ]);

  const {
    formattedData: period,
    isLoading: periodLoading,
    planYearOptions,
    monthOptions,
    defaultPeriod,
  } = useGetSpendLimitPeriod(accountId);
  const {
    formattedData: periodRemaining,
    isLoading: periodRemainingLoading,
    isError,
  } = useGetSpendLimitPeriodRemaining(accountId, selectedPeriod);

  const options = useMemo<NodeOption[]>(() => {
    if (period?.spendPeriodType === SpendPeriodType.PLAN_YEAR) {
      return planYearOptions as NodeOption[];
    }
    if (period?.spendPeriodType === SpendPeriodType.MONTHLY) {
      return monthOptions as NodeOption[];
    }
    return [];
  }, [period?.spendPeriodType, planYearOptions, monthOptions]);

  const updateGagueChartValue = useCallback((remaining: number, spendLimit: number) => {
    const percentageSpent = spendLimit ? ((spendLimit - remaining) / spendLimit) * 100 : 0;
    setGagueChartValue([
      {
        percents: percentageSpent,
        color: theme.colors.cardTypes.TRANSIT,
      },
    ]);
  }, []);

  useEffect(() => {
    if (defaultPeriod) {
      setSelectedPeriod(defaultPeriod);
    }
  }, [defaultPeriod]);

  useEffect(() => {
    if (periodRemaining) {
      updateGagueChartValue(periodRemaining.remaining, periodRemaining.spendLimit);
    }
    if (isError) {
      updateGagueChartValue(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodRemaining?.remaining, periodRemaining?.spendLimit, isError, updateGagueChartValue]);

  return {
    gagueChartValue,
    periodRemaining,
    periodRemainingLoading,
    periodLoading,
    options,
    selectedPeriod,
    handleSelectePeriod: setSelectedPeriod,
  };
};

export default useSpendPeriodTracker;
